import React from 'react';
import Router from 'next/router';
import { useIntl } from 'react-intl';
import { AppCard } from '../components/shared';
import {
  PatientForm,
  SignInFooter,
  DuplicateUserError,
  RegisterBreadcrumb,
} from '../components/register';
import { LockedUser } from '../components/login';
import {
  SimplePatient,
  RegisterError,
  PatientSubmitError,
} from '../interfaces/register';
import createSimplePatientAsync from '../api/register/createSimplePatientAsync';
import {
  USER_REGISTERED_ERROR,
  DUPLICATE_USER_ERROR,
  LOCKED_USER_ERROR,
  REGISTER_ERROR_URL,
} from '../constants/register';
import {
  REGISTRATION,
  CREATE_ACCOUNT,
  SUCCESS,
  FAILED,
} from '../constants/events';
import { event } from '../lib/analytics';
import toast from '../utils/toast';
import { formatDate } from '../utils/shared/normalize';

const PatientFormContainer = () => {
  const { formatMessage: f } = useIntl();

  const onSubmit = (
    values: SimplePatient
  ): Promise<void | PatientSubmitError> => {
    return createSimplePatientAsync({
      ...values,
      acceptTerms: true,
      username: values.email,
    }).then(
      ({
        error,
        simplePatient,
      }: {
        error?: RegisterError;
        simplePatient?: { token: string };
      }): void | PatientSubmitError => {
        if (error) {
          event({
            action: CREATE_ACCOUNT,
            category: REGISTRATION,
            parameters: { status: FAILED, errorType: error.code },
          });
          const { dobDay, dobMonth, dobYear } = values;
          const birthdate = formatDate(
            `${dobMonth}/${dobDay}/${dobYear}`,
            'iso'
          );

          const actionData = {
            controlName: 'create-account-main-button',
            controlText: f({ id: 'register.createAccount' }),
          };
          const errorData = {
            errorLocation: 'Register-CreateAnAccount',
            errorType: 'Validation',
            errorCode: error?.code ?? '',
            errorCause: '',
          };

          if (error.code === DUPLICATE_USER_ERROR) {
            errorData.errorCause =
              'You have multiple accounts with that email and date of birth.';
            toast.error(
              <DuplicateUserError email={values.email} birthdate={birthdate} />
            );
            return undefined;
          }
          if (error.code === LOCKED_USER_ERROR) {
            errorData.errorCause = f({ id: 'lockedUserError.toaster' });
            toast.error(
              <LockedUser username={values.email} birthdate={birthdate} />
            );
            return undefined;
          }
          if (error.code === USER_REGISTERED_ERROR) {
            errorData.errorCause = f({
              id: 'accountRecovery.alreadyRegistered',
            });
            window.createErrorEvent(actionData, errorData);

            Router.push(
              {
                pathname: REGISTER_ERROR_URL,
                query: { userId: error.userId },
              },
              REGISTER_ERROR_URL
            );
            return undefined;
          }
          window.createErrorEvent(actionData, errorData);
          return error.message;
        }

        if (simplePatient) {
          event({
            action: CREATE_ACCOUNT,
            category: REGISTRATION,
            parameters: { status: SUCCESS },
          });

          const { token } = simplePatient;

          Router.push(`/profile?token=${token}`);
        }
        return undefined;
      }
    );
  };

  return (
    <AppCard>
      <RegisterBreadcrumb
        heading={f({ id: 'register.createAccountHeading' })}
      />

      <PatientForm onSubmit={onSubmit} />

      <SignInFooter />
    </AppCard>
  );
};

export default PatientFormContainer;
